/**
 * The Documents tab: Document library list + WYSIWYG editor.
 *
 * Promotes Sources to top-level navigation, eliminating nested subtabs.
 * Clinical content is rendered from per-patient documents; nothing is logged (HIPAA).
 */
import React from "react";
import { ErrorBoundary } from "../ErrorBoundary";
import DocumentsPanel from "./DocumentsPanel";
import type { SourceReveal } from "../blueprint/citationResolve";

/** Props for {@link DocumentsTab}. */
interface DocumentsTabProps {
  /** The active patient id. */
  patientId: string;
  /**
   * A pending citation reveal (from "Go to source" on another tab).
   * For derivative citations (`openInEditor`), forwards to DocumentsPanel.
   */
  reveal?: SourceReveal | null;
  /** Clear the pending reveal. */
  onClearReveal?: () => void;
}

/**
 * Render the Documents tab for a patient.
 *
 * @param props - The active patient id + an optional source-citation reveal.
 * @returns The Documents tab element.
 */
export const DocumentsTab: React.FC<DocumentsTabProps> = ({
  patientId,
  reveal = null,
}) => {
  const openInEditor = reveal?.target.openInEditor === true;

  return (
    <div className="flex flex-col h-full bg-slate-50">
      <div className="flex-1 overflow-hidden">
        <ErrorBoundary context="documents-tab">
          <DocumentsPanel
            key={patientId}
            patientId={patientId}
            openDocumentId={
              openInEditor ? (reveal?.target.sourceId ?? null) : null
            }
            openKey={openInEditor ? reveal?.key : undefined}
          />
        </ErrorBoundary>
      </div>
    </div>
  );
};

export default DocumentsTab;
